import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    FaHeart, FaPlus, FaStar, FaRegStar, FaPen, FaArchive,
    FaShoppingBag, FaCheckCircle, FaBoxOpen,
    FaFileAlt, FaChartBar, FaSlidersH,
} from "react-icons/fa";
import { WISHLIST_CATEGORY_CONSTANTS, WISHLIST_ITEM_CONSTANTS } from "../_MODULES/_API";
import { WishlistCategoryModal } from "../_UI/WishlistCategoryModal";
import { WishlistItemModal } from "../_UI/WishlistItemModal";
import { WishlistCategoryWorkspaceModal } from "../_UI/WishlistCategoryWorkspaceModal";
import { onWishlistTotalsLabel } from "../_UTILS/wishlist.util";
import { PageHeader } from "../../../../../_CORE/APP/Templates/_UI/PageHeader/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { Card, EmptyState, Skeleton } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { FieldDate, FieldInput, FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { ViewLayout } from "../../../../_UI/ViewLayout";
import { StatisticsModal } from "../../../../_UI/StatisticsModal";
import { AdvancedFilterPanel, onDateMatches } from "../../../../_UI/AdvancedFilterPanel";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { ViewPagination } from "../../../../_UI/ViewPagination";
import { usePagedList } from "../../../../_UI/usePagedList";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { onSilentPatch } from "../../../../_UI/onSilentPatch";
import { onApiAction } from "../../../../_UI/onApiAction";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { CredentialExportPickerModal } from "../../../../_UI/CredentialExportPickerModal";
import { downloadCredentialPdf, downloadCredentialTxt } from "../../../../_UI/CredentialDownload";
import { wishlistCategoryExportRows, wishlistItemExportRows } from "../_UTILS/wishlistShare.util";
import "../_UI/_Styles/Wishlist.css";

export const WishlistViewMain = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [search, setSearch] = useState("");
    const [categories, setCategories] = useState([]);
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [itemModalOpen, setItemModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [itemDefaultCategoryId, setItemDefaultCategoryId] = useState("");
    const [workspaceCategoryId, setWorkspaceCategoryId] = useState(null);
    const [workspaceFocusItemId, setWorkspaceFocusItemId] = useState(null);
    const [exportPicker, setExportPicker] = useState(null);
    const [statisticsOpen, setStatisticsOpen] = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [advanced, setAdvanced] = useState({ favorite: "ALL", purchase: "ALL", store: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null });
    const dialogClosingRef = useRef(false);

    const { apiGet: getCategories, useApiData: categoryData, useApiLoading: categoriesLoading } = useApiQuery({
        minTimeQuery: 200, enablePagination: false, startLoading: true,
    });
    const { apiGet: getItems, useApiData: itemData, useApiLoading: itemsLoading } = useApiQuery({
        minTimeQuery: 200, enablePagination: false, startLoading: true,
    });

    useEffect(() => {
        getCategories({ endpoint: WISHLIST_CATEGORY_CONSTANTS.LIST });
        getItems({ endpoint: WISHLIST_ITEM_CONSTANTS.LIST });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { setCategories(categoryData ?? []); }, [categoryData]);
    useEffect(() => { setItems(itemData ?? []); }, [itemData]);

    const categoryItems = (categoryId) => items.filter((item) => String(item.categoryId) === String(categoryId));
    const workspaceCategory = useMemo(() => categories.find((category) => String(category.id) === String(workspaceCategoryId)) || null, [categories, workspaceCategoryId]);

    const updateRouteState = (patch = {}) => {
        const next = new URLSearchParams(window.location.search);
        Object.entries(patch).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") next.delete(key);
            else next.set(key, String(value));
        });
        setSearchParams(next, { replace: true });
    };

    useEffect(() => {
        const routedCategory = searchParams.get("category");
        if (!routedCategory) {
            setWorkspaceCategoryId(null);
            setWorkspaceFocusItemId(null);
            return;
        }
        if (!categories.some((category) => String(category.id) === String(routedCategory))) return;
        setWorkspaceCategoryId((current) => String(current || "") === String(routedCategory) ? current : routedCategory);
    }, [categories, searchParams]);

    useEffect(() => {
        const dialog = searchParams.get("dialog");
        if (dialogClosingRef.current) {
            if (!dialog) dialogClosingRef.current = false;
            return;
        }
        if (!dialog) return;
        if (dialog === "category-new") { setEditing(null); setModalOpen(true); return; }
        if (dialog.startsWith("category:")) {
            const id = dialog.slice("category:".length);
            const category = categories.find((row) => String(row.id) === String(id));
            if (category) { setEditing(category); setModalOpen(true); }
            return;
        }
        if (dialog === "item-new") { setEditingItem(null); setItemDefaultCategoryId(""); setItemModalOpen(true); return; }
        if (dialog.startsWith("item-new:")) {
            const categoryId = dialog.slice("item-new:".length);
            if (categories.some((row) => String(row.id) === String(categoryId))) {
                setEditingItem(null); setItemDefaultCategoryId(categoryId); setItemModalOpen(true);
            }
            return;
        }
        if (dialog.startsWith("item:")) {
            const id = dialog.slice("item:".length);
            const item = items.find((row) => String(row.id) === String(id));
            if (item) { setEditingItem(item); setItemDefaultCategoryId(item.categoryId || ""); setItemModalOpen(true); }
        }
    }, [searchParams, categories, items]);

    const closeWorkspace = () => {
        const next = new URLSearchParams(window.location.search);
        ["category", "item", "preview", "advanced", "dialog"].forEach((key) => next.delete(key));
        setSearchParams(next, { replace: true });
        setWorkspaceCategoryId(null);
        setWorkspaceFocusItemId(null);
    };

    const visible = useMemo(() => [...categories]
        .sort((a, b) => (Number(!!b.isFavorite) - Number(!!a.isFavorite))
            || Number(a.position ?? 0) - Number(b.position ?? 0)
            || String(a.name ?? "").localeCompare(String(b.name ?? "")))
        .filter((category) => {
            const children = categoryItems(category.id);
            if (advanced.favorite !== "ALL" && (!!category.isFavorite !== (advanced.favorite === "YES"))) return false;
            if (advanced.purchase === "PENDING" && !children.some((item) => !item.isPurchased)) return false;
            if (advanced.purchase === "PURCHASED" && !children.some((item) => item.isPurchased)) return false;
            if (String(advanced.store || "").trim() && !children.some((item) => onMatchesSearch(advanced.store, [item.store]))) return false;
            const dateValue = advanced.dateField === "CREATED" ? category.createdAt : category.updatedAt;
            if (!onDateMatches(dateValue, advanced.dateRange, advanced.dateExact)) return false;
            return onMatchesSearch(search, [
                category.name, category.description,
                ...children.flatMap((item) => [item.name, item.store, item.description, item.url, item.notes]),
            ]);
        }), [categories, items, search, advanced]); // eslint-disable-line react-hooks/exhaustive-deps

    const advancedCount = [
        advanced.favorite !== "ALL", advanced.purchase !== "ALL", String(advanced.store || "").trim(),
        advanced.dateRange?.[0] || advanced.dateRange?.[1], advanced.dateExact,
    ].filter(Boolean).length;
    const clearAdvanced = () => setAdvanced({ favorite: "ALL", purchase: "ALL", store: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null });

    const pager = usePagedList(visible, 6, "wishlistPage");
    const scopedItems = useMemo(() => items.filter((item) => categories.some((category) => String(category.id) === String(item.categoryId))), [items, categories]);
    const purchased = scopedItems.filter((item) => item.isPurchased);
    const pending = scopedItems.filter((item) => !item.isPurchased);

    const openCreate = () => { setEditing(null); setModalOpen(true); updateRouteState({ dialog: "category-new" }); };
    const openEdit = (category) => { setEditing(category); setModalOpen(true); updateRouteState({ dialog: `category:${category.id}` }); };
    const openWorkspace = (category) => {
        setWorkspaceFocusItemId(null);
        setWorkspaceCategoryId(category.id);
        const next = new URLSearchParams(window.location.search);
        next.set("category", String(category.id));
        next.delete("item");
        next.delete("preview");
        next.delete("dialog");
        setSearchParams(next, { replace: true });
    };
    const openNewItem = (category) => {
        setEditingItem(null);
        setItemDefaultCategoryId(category?.id || "");
        setItemModalOpen(true);
        updateRouteState({ dialog: category?.id ? `item-new:${category.id}` : "item-new" });
    };
    const openEditItem = (item) => {
        setEditingItem(item);
        setItemDefaultCategoryId(item?.categoryId || "");
        setItemModalOpen(true);
        updateRouteState({ dialog: `item:${item.id}` });
    };

    const toggleFavorite = async (category) => {
        const previous = !!category.isFavorite;
        const next = !previous;
        setCategories((rows) => rows.map((row) => row.id === category.id ? { ...row, isFavorite: next } : row));
        const response = await onSilentPatch({
            endpoint: WISHLIST_CATEGORY_CONSTANTS.UPDATE, id: category.id,
            data: { isFavorite: next }, errorMessage: "No se pudo guardar el destacado de la categoría.",
        });
        if (!response.success) setCategories((rows) => rows.map((row) => row.id === category.id ? { ...row, isFavorite: previous } : row));
    };

    const toggleItemFavorite = async (item) => {
        const previous = !!item.isFavorite;
        const next = !previous;
        setItems((rows) => rows.map((row) => row.id === item.id ? { ...row, isFavorite: next } : row));
        const response = await onSilentPatch({
            endpoint: WISHLIST_ITEM_CONSTANTS.UPDATE, id: item.id,
            data: { isFavorite: next }, errorMessage: "No se pudo guardar el destacado del artículo.",
        });
        if (!response.success) setItems((rows) => rows.map((row) => row.id === item.id ? { ...row, isFavorite: previous } : row));
    };

    const toggleItemPurchased = async (item) => {
        const previous = !!item.isPurchased;
        const next = !previous;
        setItems((rows) => rows.map((row) => row.id === item.id ? { ...row, isPurchased: next } : row));
        const response = await onSilentPatch({
            endpoint: WISHLIST_ITEM_CONSTANTS.UPDATE, id: item.id,
            data: { isPurchased: next }, errorMessage: "No se pudo actualizar el estado de compra.",
        });
        if (!response.success) setItems((rows) => rows.map((row) => row.id === item.id ? { ...row, isPurchased: previous } : row));
    };

    const archiveItem = async (item) => {
        const ok = await confirm.danger("Archivar artículo", `Se archivará “${item.name}”. Después podés recuperarlo desde Archivados.`, { confirmText: "Archivar" });
        if (!ok) return;
        setItems((rows) => rows.filter((row) => row.id !== item.id));
        const response = await onApiAction({
            method: "PATCH", endpoint: WISHLIST_ITEM_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: item.id }, successMessage: "Artículo archivado",
        });
        if (!response.success) setItems((rows) => [item, ...rows]);
    };

    const archiveCategory = async (category) => {
        const ok = await confirm.danger(
            "Archivar categoría",
            `Se archivará “${category.name}”. Sus artículos se conservan. La categoría y sus artículos quedan disponibles en Archivados para restaurarlos.`,
        );
        if (!ok) return;
        setCategories((rows) => rows.filter((row) => row.id !== category.id));
        if (String(workspaceCategoryId || "") === String(category.id)) setWorkspaceCategoryId(null);
        const response = await onApiAction({
            method: "PATCH", endpoint: WISHLIST_CATEGORY_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: category.id }, successMessage: "Categoría archivada",
        });
        if (!response.success) setCategories((rows) => [category, ...rows]);
    };

    const loading = categoriesLoading || itemsLoading;

    return (
        <ViewLayout
            header={<PageHeader
                title="Wish List"
                description="Guardá cosas que querés comprar, agrupadas por categoría, con precio, tienda y link."
                icon={<FaHeart />}
                actions={<>
                    <AppButton variant="outline" tone="neutral" icon={<FaChartBar />} isIconOnly title="Estadísticas" aria-label="Estadísticas" onClick={() => setStatisticsOpen(true)} />
                    <AppButton icon={<FaPlus />} onClick={openCreate}>Nueva categoría</AppButton>
                </>} />}
            toolbar={<ViewToolbar
                filters={<>
                    <SearchFilter value={search} onChange={setSearch}
                        total={categories.length} matches={visible.length}
                        placeholder="Categoría, artículo, tienda o link" />
                    <AppButton variant={advancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />} onClick={() => setAdvancedOpen((current) => !current)}>
                        {advancedCount ? `Avanzado (${advancedCount})` : "Avanzado"}
                    </AppButton>
                </>}
                expandedContent={<AdvancedFilterPanel isOpen={advancedOpen} activeCount={advancedCount} onClear={clearAdvanced}>
                    <FieldSelect id="wishlist_adv_favorite" label="Destacado" value={advanced.favorite}
                        onChange={(event) => setAdvanced((current) => ({ ...current, favorite: event.target.value }))}
                        options={[{ value: "ALL", label: "Todos" }, { value: "YES", label: "Sólo destacados" }, { value: "NO", label: "No destacados" }]} />
                    <FieldSelect id="wishlist_adv_purchase" label="Artículos" value={advanced.purchase}
                        onChange={(event) => setAdvanced((current) => ({ ...current, purchase: event.target.value }))}
                        options={[{ value: "ALL", label: "Todos" }, { value: "PENDING", label: "Con pendientes" }, { value: "PURCHASED", label: "Con comprados" }]} />
                    <FieldInput id="wishlist_adv_store" label="Tienda" value={advanced.store}
                        onChange={(event) => setAdvanced((current) => ({ ...current, store: event.target.value }))} placeholder="Mercado Libre, Amazon..." />
                    <FieldSelect id="wishlist_adv_date_field" label="Fecha" value={advanced.dateField}
                        onChange={(event) => setAdvanced((current) => ({ ...current, dateField: event.target.value }))}
                        options={[{ value: "UPDATED", label: "Última actualización" }, { value: "CREATED", label: "Fecha de creación" }]} />
                    <FieldDate id="wishlist_adv_from" label="Desde" value={advanced.dateRange?.[0] || null}
                        onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [date || null, current.dateRange?.[1] || null], dateExact: null }))} isClearable />
                    <FieldDate id="wishlist_adv_to" label="Hasta" value={advanced.dateRange?.[1] || null}
                        onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [current.dateRange?.[0] || null, date || null], dateExact: null }))} isClearable />
                    <FieldDate id="wishlist_adv_exact" label="Fecha exacta" value={advanced.dateExact}
                        onChange={(date) => setAdvanced((current) => ({ ...current, dateExact: date || null, dateRange: date ? [null, null] : current.dateRange }))} isClearable />
                </AdvancedFilterPanel>}
                total={pager.total} totalSingular="categoría" totalPlural="categorías" perPage={pager.usePerPage} onChangePerPage={pager.onChangePerPage}
                exportColumns={[
                    { key: "name", label: "Categoría" },
                    { key: "description", label: "Descripción" },
                    { key: "items", label: "Artículos" },
                    { key: "purchased", label: "Comprados" },
                    { key: "total", label: "Total" },
                ]}
                exportRows={visible.map((category) => {
                    const rows = categoryItems(category.id);
                    return {
                        name: category.name,
                        description: category.description ?? "",
                        items: rows.length,
                        purchased: rows.filter((item) => item.isPurchased).length,
                        total: onWishlistTotalsLabel(rows),
                    };
                })}
                exportName="wishlist-categorias" /> }>

            {loading && <div className="LStudio__wishlist_grid"><Skeleton variant="box" height={210} count={3} /></div>}

            {!loading && visible.length === 0 && <EmptyState size="lg" icon={<FaHeart />}
                title={categories.length ? "No hay coincidencias" : "Tu Wish List está vacía"}
                description={categories.length ? null : "Creá una categoría y empezá a guardar artículos."}
                action={categories.length
                    ? <AppButton variant="outline" tone="neutral" onClick={() => { setSearch(""); clearAdvanced(); }}>Limpiar filtros</AppButton>
                    : <AppButton icon={<FaPlus />} onClick={openCreate}>Nueva categoría</AppButton>} />}

            {!loading && visible.length > 0 && <div className="LStudio__wishlist_grid">
                {pager.paged.map((category) => {
                    const rows = categoryItems(category.id);
                    const bought = rows.filter((item) => item.isPurchased).length;
                    return <div key={category.id} className="LStudio__wishlist_category_shell" role="button" tabIndex={0}
                        onClick={() => openWorkspace(category)}
                        onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); openWorkspace(category); } }}>
                        <Card className="LStudio__wishlist_category_card" data-favorite={!!category.isFavorite}>
                            <div className="LStudio__wishlist_card_head">
                                <span className="LStudio__wishlist_color" style={{ background: category.color || "var(--LS__accent, #e83a52)" }} />
                                <div className="LStudio__wishlist_card_title">
                                    <strong>
                                        <span className="LStudio__wishlist_title_text">{category.icon ? `${category.icon} ` : ""}{category.name}</span>
                                        {category.isFavorite && <FaStar className="LStudio__app_record_star" title="Destacado" />}
                                    </strong>
                                </div>
                                <div onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                                    <CardActionMenu title="Acciones de la categoría" items={[
                                        { key: "export", icon: <FaFileAlt />, label: "Guardar / exportar…", onClick: () => setExportPicker({ category, rows: wishlistCategoryExportRows(category, rows) }) },
                                        { divider: true },
                                        { key: "favorite", icon: category.isFavorite ? <FaStar /> : <FaRegStar />, label: category.isFavorite ? "Quitar de destacados" : "Destacar", onClick: () => toggleFavorite(category) },
                                        { key: "edit", icon: <FaPen />, label: "Editar", onClick: () => openEdit(category) },
                                        { divider: true },
                                        { key: "archive", icon: <FaArchive />, label: "Archivar", tone: "danger", onClick: () => archiveCategory(category) },
                                    ]} />
                                </div>
                            </div>

                            {category.description && <p className="LStudio__wishlist_description">{category.description}</p>}
                            <div className="LStudio__wishlist_category_stats">
                                <span><b>{rows.length}</b> artículos</span>
                                <span><b>{bought}</b> comprados</span>
                                <span><b>{Math.max(0, rows.length - bought)}</b> pendientes</span>
                            </div>
                            <div className="LStudio__wishlist_total_line">
                                <span>Total</span><strong>{onWishlistTotalsLabel(rows)}</strong>
                            </div>
                        </Card>
                    </div>;
                })}
            </div>}

            <ViewPagination page={pager.usePage} totalPages={pager.totalPages}
                total={pager.total} perPage={pager.usePerPage} onChange={pager.onChangePage} />

            <StatisticsModal isOpen={statisticsOpen} onClose={() => setStatisticsOpen(false)}
                title="Estadísticas · Wish List" description="Resumen general de lo que guardaste y compraste."
                metrics={[
                    { key: "categories", label: "Categorías", value: categories.length, icon: <FaBoxOpen />, tone: "neutral", isLoading: loading },
                    { key: "pending", label: "Pendientes", value: pending.length, icon: <FaShoppingBag />, tone: "accent", footer: onWishlistTotalsLabel(pending), isLoading: loading },
                    { key: "purchased", label: "Comprados", value: purchased.length, icon: <FaCheckCircle />, tone: "ok", footer: onWishlistTotalsLabel(purchased), isLoading: loading },
                    { key: "saved", label: "Total guardado", value: scopedItems.length, icon: <FaHeart />, tone: "neutral", footer: onWishlistTotalsLabel(scopedItems), isLoading: loading },
                    { key: "favorite-categories", label: "Categorías destacadas", value: categories.filter((category) => category.isFavorite).length, icon: <FaStar />, tone: "neutral", isLoading: loading },
                ]} />

            <CredentialExportPickerModal isOpen={!!exportPicker}
                title={exportPicker?.item ? `Guardar · ${exportPicker.item.name}` : exportPicker?.category ? `Guardar · ${exportPicker.category.name}` : "Guardar Wish List"}
                rows={exportPicker?.rows ?? []}
                onClose={() => setExportPicker(null)}
                onExport={(format, selectedRows) => {
                    const item = exportPicker?.item;
                    const category = exportPicker?.category;
                    if (!item && !category) return;
                    const label = item?.name || category?.name || "wishlist";
                    const payload = { title: `Wish List · ${label}`, rows: selectedRows, fileName: `wishlist-${label}` };
                    if (format === "pdf") downloadCredentialPdf(payload); else downloadCredentialTxt(payload);
                    setExportPicker(null);
                }} />

            <WishlistCategoryWorkspaceModal
                isOpen={!!workspaceCategory}
                category={workspaceCategory}
                items={items}
                focusItemId={workspaceFocusItemId}
                onFocusItemHandled={() => setWorkspaceFocusItemId(null)}
                onClose={closeWorkspace}
                onNewItem={openNewItem}
                onEditItem={openEditItem}
                onEditCategory={openEdit}
                onExportItem={(item, category) => setExportPicker({ item, category, rows: wishlistItemExportRows(item, category?.name || "") })}
                onToggleFavorite={toggleItemFavorite}
                onTogglePurchased={toggleItemPurchased}
                onArchiveItem={archiveItem}
                isLoading={categoriesLoading || itemsLoading}
            />

            <WishlistItemModal isOpen={itemModalOpen} item={editingItem}
                categories={categories} items={items} defaultCategoryId={itemDefaultCategoryId} isCategoryLocked={!editingItem}
                onClose={() => { dialogClosingRef.current = true; setItemModalOpen(false); setEditingItem(null); setItemDefaultCategoryId(""); updateRouteState({ dialog: null }); }}
                onSaved={(saved, isEdit) => {
                    if (!saved?.id) return;
                    setItems((rows) => isEdit
                        ? rows.map((row) => row.id === saved.id ? { ...row, ...saved } : row)
                        : [saved, ...rows]);
                    dialogClosingRef.current = true;
                    updateRouteState({ dialog: null });
                    const targetCategoryId = saved.categoryId || workspaceCategoryId;
                    setWorkspaceCategoryId(targetCategoryId);
                    setWorkspaceFocusItemId(saved.id);
                    const next = new URLSearchParams(window.location.search);
                    if (targetCategoryId) next.set("category", String(targetCategoryId));
                    next.set("item", String(saved.id));
                    next.delete("preview");
                    next.delete("dialog");
                    setSearchParams(next, { replace: true });
                }} />

            <WishlistCategoryModal isOpen={modalOpen} category={editing} categories={categories}
                onClose={() => { dialogClosingRef.current = true; setModalOpen(false); setEditing(null); updateRouteState({ dialog: null }); }}
                onSaved={(saved, isEdit) => {
                    setCategories((rows) => isEdit
                        ? rows.map((row) => row.id === saved.id ? { ...row, ...saved } : row)
                        : [saved, ...rows]);
                    setEditing(null);
                    dialogClosingRef.current = true;
                    updateRouteState({ dialog: null });
                    if (!isEdit) { setWorkspaceCategoryId(saved.id); const next = new URLSearchParams(window.location.search); next.set("category", String(saved.id)); next.delete("dialog"); setSearchParams(next, { replace: true }); }
                }} />
        </ViewLayout>
    );
};
